import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Form, Input, DatePicker, Select, Button, Card, message, Row, Col } from 'antd';
import { apiClient } from '../../config/apiClient';
import { LoginContext } from '../../context/loginContext.js';
import { categoriaLabel } from '../../utils/categoriaLabel';

const Registro = () => {
  const [form] = Form.useForm();
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useContext(LoginContext);

  // Datos provenientes de Auth0 (si el usuario fue redirigido desde el callback)
  const auth0Data = location.state?.auth0Data || null;

  // Si no hay datos de Auth0 (acceso directo), redirigir a /jugador-acceso
  useEffect(() => {
    if (!auth0Data) {
      navigate('/jugador-acceso', { replace: true });
    }
  }, [auth0Data, navigate]);

  // Pre-llenar el formulario con datos de Auth0
  useEffect(() => {
    if (auth0Data) {
      form.setFieldsValue({
        nombre: auth0Data.nombre || '',
        apellido: auth0Data.apellido || '',
        email: auth0Data.email || '',
      });
    }
  }, [auth0Data, form]);

  useEffect(() => {
    const loadCategorias = async () => {
      try {
        const response = await apiClient.get('/categorias');
        if (response.ok) {
          const data = await response.json();
          setCategorias(data.filter((c) => c.cat_activo));
        }
      } catch (error) {
        console.error('Error al cargar categorías:', error);
      }
    };
    loadCategorias();
  }, []);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const dataToSend = {
        nombre: values.nombre,
        apellido: values.apellido,
        fechaNacimiento: values.fechaNacimiento.format('YYYY-MM-DD'),
        sexo: values.sexo,
        email: values.email,
        telefono: values.telefono,
        categoria: values.categoria,
      };

      // Usar el nuevo endpoint que registra y devuelve JWT
      const response = await apiClient.post('/club/jugadores/registro-y-login', dataToSend);
      const responseData = await response.json();

      if (response.ok) {
        // Iniciar sesión automáticamente con los datos recibidos
        const { token, refreshToken, usuario } = responseData;

        const userData = {
          usu_id: usuario.usu_id,
          usu_usuario: usuario.usu_usuario,
          usu_tipousuario: usuario.usu_tipousuario,
          tiu_descripcion: usuario.tiu_descripcion,
          token,
          refreshToken,
          id: usuario.usu_id,
        };

        // Guardar refreshToken
        if (refreshToken) {
          try {
            localStorage.setItem('refreshToken', refreshToken);
          } catch (err) {
            console.warn('No se pudo guardar refreshToken:', err);
          }
        }

        // Marcar que acabamos de loguearnos (evita que el contexto haga logout)
        sessionStorage.setItem('justLoggedIn', 'true');

        // Guardar usuario en localStorage y en el contexto
        localStorage.setItem('user', JSON.stringify(userData));
        await login(userData);

        message.success('¡Registro exitoso! Bienvenido/a.');

        // Redirigir al panel del jugador
        setTimeout(() => {
          window.location.href = '/#/jugador/nuevo-turno';
        }, 300);
      } else {
        message.error(responseData.message || 'Error al registrar');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al registrar');
    } finally {
      setLoading(false);
    }
  };

  // Si no hay auth0Data, no renderizar nada (se va a redirigir)
  if (!auth0Data) return null;

  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    minHeight: '100vh',
    background: '#1a1a1a',
    padding: window.innerWidth <= 768 ? '12px' : '20px',
    paddingTop: window.innerWidth <= 768 ? '40px' : '60px',
    paddingBottom: window.innerWidth <= 768 ? '12px' : '20px',
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: '100vw',
    overflowX: 'hidden',
  };

  const cardStyle = { width: '100%', maxWidth: 600, marginBottom: 16 };

  return (
    <div style={containerStyle}>
      <div style={{ width: '100%', maxWidth: 600 }}>
        <Card style={{ ...cardStyle, background: '#2d2d2d', borderColor: '#404040' }}>
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <h1 style={{ marginBottom: '8px', color: '#ffffff' }}>Completá tu registro</h1>
            <p style={{ color: '#b0b0b0' }}>
              Es tu primera vez ingresando. Completá estos datos para registrarte como jugador del club.
            </p>
          </div>

          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="nombre"
                  label="Nombre"
                  rules={[
                    { required: true, message: 'El nombre es requerido' },
                    { max: 100, message: 'El nombre no puede exceder 100 caracteres' },
                  ]}
                >
                  <Input placeholder="Ingrese el nombre" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="apellido"
                  label="Apellido"
                  rules={[
                    { required: true, message: 'El apellido es requerido' },
                    { max: 100, message: 'El apellido no puede exceder 100 caracteres' },
                  ]}
                >
                  <Input placeholder="Ingrese el apellido" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="fechaNacimiento"
              label="Fecha de Nacimiento"
              rules={[{ required: true, message: 'La fecha de nacimiento es requerida' }]}
            >
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Seleccione la fecha de nacimiento" />
            </Form.Item>

            <Form.Item name="sexo" label="Sexo" rules={[{ required: true, message: 'El sexo es requerido' }]}>
              <Select placeholder="Seleccione el sexo">
                <Select.Option value="Masculino">Masculino</Select.Option>
                <Select.Option value="Femenino">Femenino</Select.Option>
                <Select.Option value="Otro">Otro</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'El email es requerido' },
                { type: 'email', message: 'El formato del email no es válido' },
              ]}
            >
              <Input disabled style={{ color: '#b0b0b0', backgroundColor: '#383838' }} />
            </Form.Item>

            <Form.Item
              name="telefono"
              label="Teléfono (WhatsApp)"
              extra={<span style={{ color: '#888' }}>Sin 0, sin 15 y sin espacios. Ej: 3534123456</span>}
              rules={[
                { required: true, message: 'El teléfono es requerido' },
                { pattern: /^[1-9]\d{9}$/, message: 'Ingresá 10 dígitos. No puede empezar con 0, no incluyas el 15 ni espacios.' },
              ]}
            >
              <Input placeholder="Ej: 3534123456" maxLength={10} />
            </Form.Item>

            <Form.Item name="categoria" label="Categoría" rules={[{ required: true, message: 'La categoría es requerida' }]}>
              <Select
                placeholder="Seleccione la categoría"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              >
                {categorias.map((cat) => (
                  <Select.Option key={cat.cat_id} value={cat.cat_id} label={categoriaLabel(cat)}>
                    {categoriaLabel(cat)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={loading}
                style={{
                  height: 48,
                  fontSize: 16,
                  fontWeight: 600,
                  borderRadius: 8,
                  background: '#22c55e',
                  borderColor: '#22c55e',
                }}
              >
                Completar registro e ingresar
              </Button>
            </Form.Item>
          </Form>
        </Card>

        <div
          style={{
            textAlign: 'center',
            marginTop: '40px',
            paddingBottom: '20px',
            color: '#b0b0b0',
            fontSize: '14px',
          }}
        >
          <a
            href="https://genosoft.com.ar/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#b0b0b0', textDecoration: 'none' }}
          >
            Desarrollado por Geno Software
          </a>
        </div>
      </div>
    </div>
  );
};

export default Registro;
